// ========================================
// PAGE PROJECT DETAIL - VERSION WOW
// Expérience immersive de lecture
// ========================================

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../../components/common/Navigation';
import Footer from '../../components/common/Footer';
import LoadingScreen from '../../components/common/LoadingScreen';
import { useProjects } from '../../hooks';
import { 
  FiArrowLeft, 
  FiCalendar, 
  FiUser, 
  FiExternalLink,
  FiArrowRight,
  FiShare2,
  FiLink,
  FiCheck,
  FiLayers,
  FiMapPin,
  FiAward,
  FiChevronLeft,
  FiChevronRight,
  FiX
} from 'react-icons/fi';
import { FaLinkedin, FaTwitter, FaFacebook, FaWhatsapp } from 'react-icons/fa';
import './ProjectDetail.css';

// ============================================
// FONCTION DE NETTOYAGE DU CONTENU HTML
// ============================================
const cleanHtmlContent = (html) => {
  if (!html) return '';
  
  let cleaned = html;
  cleaned = cleaned.replace(/&nbsp;/g, ' ');
  cleaned = cleaned.replace(/\u00A0/g, ' ');
  cleaned = cleaned.replace(/  +/g, ' ');
  cleaned = cleaned.replace(/ql-align-justify/g, 'ql-align-left');
  cleaned = cleaned.replace(/style\s*=\s*["'][^"']*text-align\s*:\s*justify[^"']*["']/gi, '');
  
  return cleaned;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
const ProjectDetail = () => {
  const { slug } = useParams();

  // ========== HOOKS ==========
  const { projects, loading, getProjectBySlug } = useProjects();

  // ========== ÉTATS ==========
  const [project, setProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  // ========== PROJETS NAVIGATION ==========
  const { prevProject, nextProject } = useMemo(() => {
    if (!projects?.length || !project) {
      return { prevProject: null, nextProject: null };
    }

    const currentIndex = projects.findIndex(p => p.id === project.id);
    
    return {
      prevProject: currentIndex > 0 ? projects[currentIndex - 1] : null,
      nextProject: currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null
    };
  }, [projects, project]);

  // ========== CHARGER LE PROJET ==========
  useEffect(() => {
    const loadProject = async () => {
      setProjectLoading(true);
      setError(null);

      try {
        if (projects?.length) {
          const found = projects.find(p => p.slug === slug);
          if (found) {
            setProject(found);
            setProjectLoading(false);
            return;
          }
        }

        const result = await getProjectBySlug(slug);
        if (result.success && result.data) {
          setProject(result.data);
        } else {
          setError('Projet non trouvé');
        }
      } catch (err) {
        console.error('Erreur chargement projet:', err);
        setError('Erreur lors du chargement du projet');
      }

      setProjectLoading(false);
    };

    if (slug) {
      loadProject();
    }
  }, [slug, projects, getProjectBySlug]);

  // ========== SCROLL TO TOP ==========
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // ========== HELPERS ==========
  const getShareUrl = () => window.location.href;

  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(project?.title || '')}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${project?.title || ''} - ${getShareUrl()}`)}`
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur copie:', err);
    }
  };

  // Gallery navigation
  const allImages = useMemo(() => {
    if (!project) return [];
    const images = [];
    if (project.mainImage) images.push(project.mainImage);
    if (project.gallery?.length) images.push(...project.gallery);
    return images;
  }, [project]);

  const nextImage = () => {
    setActiveImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setActiveImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isGalleryOpen) return;
      if (e.key === 'Escape') setIsGalleryOpen(false);
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen, allImages.length]);

  // ========== LOADING ==========
  if (loading || projectLoading) {
    return <LoadingScreen message="Chargement du projet..." />;
  }

  // ========== ERROR ==========
  if (error || !project) {
    return (
      <div className="project-detail-wow">
        <Navigation />
        <div className="error-container-wow">
          <motion.div
            className="error-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="error-icon">
              <FiLayers />
            </div>
            <h1>Projet introuvable</h1>
            <p>Ce projet n'existe pas ou a été déplacé.</p>
            <Link to="/projets" className="back-btn-wow">
              <FiArrowLeft />
              Voir tous les projets
            </Link>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  const cleanedDescription = cleanHtmlContent(project.fullDescription);

  // ========== RENDER ==========
  return (
    <div className="project-detail-wow">
      <Navigation />

      {/* ==================== HERO SECTION ==================== */}
      <section className="project-hero-wow">
        <div className="hero-image-container">
          {project.mainImage ? (
            <img src={project.mainImage} alt={project.title} />
          ) : (
            <div className="hero-placeholder">
              <span>{project.title?.charAt(0) || 'P'}</span>
            </div>
          )}
          <div className="hero-overlay-gradient"></div>
        </div>

        <div className="hero-content-wow">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/projets" className="back-link-wow">
                <FiArrowLeft />
                <span>Retour aux projets</span>
              </Link>

              <div className="hero-badges">
                <span className="badge-category">
                  {project.categoryLabel || project.category || 'Projet'}
                </span>
                <span className={`badge-status ${project.status}`}>
                  {project.status === 'active' ? '● En cours' : '✓ Terminé'}
                </span>
                {project.isFeatured && (
                  <span className="badge-featured">
                    <FiAward />
                    Vedette
                  </span>
                )}
              </div>

              <motion.h1 
                className="hero-title-wow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                {project.title}
              </motion.h1>
              
              <motion.p 
                className="hero-excerpt"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {project.shortDescription}
              </motion.p>

              <motion.div 
                className="hero-meta-grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="meta-card">
                  <FiUser className="meta-icon" />
                  <div className="meta-info">
                    <span className="meta-label">Rôle</span>
                    <span className="meta-value">{project.role || 'Contributeur'}</span>
                  </div>
                </div>
                
                <div className="meta-card">
                  <FiCalendar className="meta-icon" />
                  <div className="meta-info">
                    <span className="meta-label">Période</span>
                    <span className="meta-value">
                      {project.yearStart || 'N/A'} - {project.yearEnd || 'Présent'}
                    </span>
                  </div>
                </div>

                {project.location && (
                  <div className="meta-card">
                    <FiMapPin className="meta-icon" />
                    <div className="meta-info">
                      <span className="meta-label">Lieu</span>
                      <span className="meta-value">{project.location}</span>
                    </div>
                  </div>
                )}

                {project.externalLink && (
                  <a 
                    href={project.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="meta-card link"
                  >
                    <FiExternalLink className="meta-icon" />
                    <div className="meta-info">
                      <span className="meta-label">Lien</span>
                      <span className="meta-value">Visiter le site</span>
                    </div>
                  </a>
                )}
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="scroll-hint"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <FiChevronLeft style={{ transform: 'rotate(-90deg)' }} />
          </motion.div>
        </motion.div>
      </section>

      {/* ==================== MAIN CONTENT ==================== */}
      <section className="project-content-wow">
        <div className="container">
          <div className="content-layout">
            
            {/* Sidebar */}
            <aside className="project-sidebar-wow">
              <div className="sidebar-sticky">
                {/* Share */}
                <div className="sidebar-card share-card">
                  <h4>
                    <FiShare2 />
                    Partager
                  </h4>
                  <div className="share-buttons-wow">
                    <a 
                      href={shareLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn-wow linkedin"
                      aria-label="LinkedIn"
                    >
                      <FaLinkedin />
                    </a>
                    <a 
                      href={shareLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn-wow twitter"
                      aria-label="Twitter"
                    >
                      <FaTwitter />
                    </a>
                    <a 
                      href={shareLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn-wow facebook"
                      aria-label="Facebook"
                    >
                      <FaFacebook />
                    </a>
                    <a 
                      href={shareLinks.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn-wow whatsapp"
                      aria-label="WhatsApp"
                    >
                      <FaWhatsapp />
                    </a>
                    <button 
                      onClick={copyToClipboard}
                      className={`share-btn-wow copy ${copied ? 'copied' : ''}`}
                      aria-label="Copier le lien"
                    >
                      {copied ? <FiCheck /> : <FiLink />}
                    </button>
                  </div>
                </div>

                {/* Quick Info */}
                <div className="sidebar-card info-card">
                  <h4>
                    <FiLayers />
                    Infos rapides
                  </h4>
                  <ul className="quick-info-list">
                    <li>
                      <span className="info-label">Catégorie</span>
                      <span className="info-value">{project.categoryLabel || project.category}</span>
                    </li>
                    <li>
                      <span className="info-label">Statut</span>
                      <span className={`info-value status ${project.status}`}>
                        {project.status === 'active' ? 'En cours' : 'Terminé'}
                      </span>
                    </li>
                    <li>
                      <span className="info-label">Durée</span>
                      <span className="info-value">
                        {project.yearStart && project.yearEnd 
                          ? `${project.yearEnd - project.yearStart} an(s)`
                          : 'En cours'
                        }
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Tags */}
                {project.technologies?.length > 0 && (
                  <div className="sidebar-card tags-card">
                    <h4>Technologies</h4>
                    <div className="tags-list">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="tag-item">{tech}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>

            {/* Main Content */}
            <main className="project-main-wow">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Description */}
                {cleanedDescription ? (
                  <div 
                    className="project-body-wow"
                    dangerouslySetInnerHTML={{ __html: cleanedDescription }}
                  />
                ) : (
                  <div className="project-body-wow">
                    <p>{project.shortDescription}</p>
                  </div>
                )}

                {/* Gallery */}
                {allImages.length > 1 && (
                  <div className="gallery-section-wow">
                    <h3>
                      <FiLayers />
                      Galerie du projet
                    </h3>
                    
                    {/* Main Gallery Image */}
                    <div className="gallery-showcase">
                      <button 
                        className="gallery-nav prev"
                        onClick={prevImage}
                        aria-label="Image précédente"
                      >
                        <FiChevronLeft />
                      </button>
                      
                      <div 
                        className="gallery-main-image"
                        onClick={() => setIsGalleryOpen(true)}
                      >
                        <img 
                          src={allImages[activeImageIndex]} 
                          alt={`${project.title} - Image ${activeImageIndex + 1}`}
                        />
                        <div className="image-counter">
                          {activeImageIndex + 1} / {allImages.length}
                        </div>
                      </div>
                      
                      <button 
                        className="gallery-nav next"
                        onClick={nextImage}
                        aria-label="Image suivante"
                      >
                        <FiChevronRight />
                      </button>
                    </div>

                    {/* Thumbnails */}
                    <div className="gallery-thumbnails">
                      {allImages.map((image, index) => (
                        <button
                          key={index}
                          className={`thumbnail ${activeImageIndex === index ? 'active' : ''}`}
                          onClick={() => setActiveImageIndex(index)}
                        >
                          <img src={image} alt={`Miniature ${index + 1}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Single image display if only main image */}
                {allImages.length === 1 && project.mainImage && (
                  <div className="single-image-section">
                    <img 
                      src={project.mainImage} 
                      alt={project.title}
                      onClick={() => setIsGalleryOpen(true)}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                )}
              </motion.div>
            </main>
          </div>
        </div>
      </section>

      {/* ==================== PROJECT NAVIGATION ==================== */}
      {(prevProject || nextProject) && (
        <section className="project-nav-wow">
          <div className="container">
            <div className="nav-grid-wow">
              {prevProject ? (
                <Link 
                  to={`/projets/${prevProject.slug}`} 
                  className="nav-card-wow prev"
                >
                  <div className="nav-card-bg">
                    {prevProject.mainImage && (
                      <img src={prevProject.mainImage} alt="" />
                    )}
                    <div className="nav-card-overlay"></div>
                  </div>
                  <div className="nav-card-content">
                    <span className="nav-direction">
                      <FiArrowLeft />
                      Projet précédent
                    </span>
                    <h4>{prevProject.title}</h4>
                    <span className="nav-category">
                      {prevProject.categoryLabel || prevProject.category}
                    </span>
                  </div>
                </Link>
              ) : (
                <div className="nav-card-wow empty"></div>
              )}

              <Link to="/projets" className="nav-center-btn">
                <FiLayers />
                <span>Tous les projets</span>
              </Link>

              {nextProject ? (
                <Link 
                  to={`/projets/${nextProject.slug}`} 
                  className="nav-card-wow next"
                >
                  <div className="nav-card-bg">
                    {nextProject.mainImage && (
                      <img src={nextProject.mainImage} alt="" />
                    )}
                    <div className="nav-card-overlay"></div>
                  </div>
                  <div className="nav-card-content">
                    <span className="nav-direction">
                      Projet suivant
                      <FiArrowRight />
                    </span>
                    <h4>{nextProject.title}</h4>
                    <span className="nav-category">
                      {nextProject.categoryLabel || nextProject.category}
                    </span>
                  </div>
                </Link>
              ) : (
                <div className="nav-card-wow empty"></div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ==================== CTA SECTION ==================== */}
      <section className="project-cta-wow">
        <div className="container">
          <motion.div
            className="cta-box-wow"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="cta-icon">
              <FiAward />
            </div>
            <h2>Ce projet vous inspire ?</h2>
            <p>Discutons de votre prochain projet et créons quelque chose d'extraordinaire ensemble.</p>
            <Link to="/a-propos#contact" className="cta-link-wow">
              <span>Me contacter</span>
              <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== LIGHTBOX ==================== */}
      {isGalleryOpen && (
        <motion.div 
          className="lightbox-wow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsGalleryOpen(false)}
        >
          <button 
            className="lightbox-close"
            onClick={() => setIsGalleryOpen(false)}
            aria-label="Fermer"
          >
            <FiX />
          </button>
          
          <button 
            className="lightbox-nav prev"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            aria-label="Image précédente"
          >
            <FiChevronLeft />
          </button>
          
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <motion.img 
              key={activeImageIndex}
              src={allImages[activeImageIndex]} 
              alt={`${project.title} - Image ${activeImageIndex + 1}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            />
          </div>
          
          <button 
            className="lightbox-nav next"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            aria-label="Image suivante"
          >
            <FiChevronRight />
          </button>
          
          <div className="lightbox-counter">
            {activeImageIndex + 1} / {allImages.length}
          </div>
        </motion.div>
      )}

      <Footer />
    </div>
  );
};

export default ProjectDetail;